import type { LocalModelDepthField } from "../../../shared/particles/particlePhotoLocalDepth";
import { encodeV2DepthField } from "../../../shared/particles/particlePhotoLocalDepth";
import {
  prepareEncryptedBinaryTransfer,
  uploadPreparedEncryptedMediaTransfer,
  type RelayRecipientDeviceKey
} from "../../../shared/platform/encryptedMediaRelay";
import type { RuntimeProbe } from "../../../shared/platform/runtime";
import { supabase } from "../../../shared/supabase/client";

interface TargetDeviceKeyRow {
  device_id: string;
  device_key_id: string;
  public_key: string;
}

function client() {
  if (!supabase) throw new Error("Supabase 尚未配置");
  return supabase;
}

function recipients(rows: TargetDeviceKeyRow[]): RelayRecipientDeviceKey[] {
  const result = rows.map((row) => ({
    recipientDeviceId: row.device_id,
    recipientKeyId: row.device_key_id,
    publicKey: row.public_key
  }));
  if (result.length === 0 || result.some((recipient) => !recipient.publicKey)) {
    throw new Error("这条星河还有成员未完成安全同步准备");
  }
  return result;
}

/**
 * Publish a small, encrypted V2 depth field. The original remains in the
 * existing original-media relay; this only carries a regenerable derivative.
 */
export async function publishV2DepthField(
  galaxyId: string,
  mediaId: string,
  field: LocalModelDepthField,
  runtime: RuntimeProbe
) {
  if (!supabase || !runtime.storageReady || !runtime.deviceId) return false;
  const database = client();
  const { data: existing, error: existingError } = await database
    .from("media_depth_assets")
    .select("id")
    .eq("media_id", mediaId)
    .eq("model_id", "depth-anything-v2-small")
    .maybeSingle();
  if (existingError) {
    if (existingError.code === "PGRST205" || existingError.code === "42P01") return false;
    throw existingError;
  }
  if (existing) return false;

  const { data: keys, error: keysError } = await database.rpc("list_media_target_device_keys", {
    target_media_id: mediaId
  });
  if (keysError) throw keysError;
  const depthAssetId = crypto.randomUUID();
  const transferId = crypto.randomUUID();
  const encoded = encodeV2DepthField(field);
  const prepared = await prepareEncryptedBinaryTransfer(
    transferId,
    depthAssetId,
    encoded.bytes,
    recipients((keys ?? []) as TargetDeviceKeyRow[])
  );
  if (prepared.sourceDeviceId !== runtime.deviceId) throw new Error("本机深度加密准备结果无效");
  const storagePath = `${galaxyId}/relay/${transferId}.bin`;
  const { error: createError } = await database.rpc("create_encrypted_v2_depth_transfer", {
    target_depth_asset_id: depthAssetId,
    target_transfer_id: transferId,
    target_media_id: mediaId,
    target_source_device_id: runtime.deviceId,
    target_storage_path: storagePath,
    target_source_sha256: prepared.sourceSha256,
    target_ciphertext_sha256: prepared.ciphertextSha256,
    target_ciphertext_size: prepared.ciphertextSize,
    target_width: encoded.width,
    target_height: encoded.height,
    target_envelopes: prepared.envelopes.map((envelope) => ({
      recipient_device_id: envelope.recipientDeviceId,
      recipient_key_id: envelope.recipientKeyId,
      envelope: envelope.envelope
    }))
  });
  if (createError) {
    // Another authorized Windows device may have generated the same field first.
    if (createError.message.includes("already has a V2 depth transfer")) return false;
    throw createError;
  }
  const { data: signedUpload, error: signError } = await database.storage
    .from("memory-relay")
    .createSignedUploadUrl(storagePath);
  if (signError) throw signError;
  const uploaded = await uploadPreparedEncryptedMediaTransfer(transferId, signedUpload.signedUrl);
  if (uploaded.ciphertextSha256 !== prepared.ciphertextSha256) throw new Error("V2 深度上传校验不一致");
  const { error: markError } = await database.rpc("mark_encrypted_media_transfer_uploaded", {
    target_transfer_id: transferId,
    observed_ciphertext_sha256: uploaded.ciphertextSha256
  });
  if (markError) throw markError;
  const { error: receiptError } = await database.rpc("record_encrypted_media_delivery_receipt", {
    target_transfer_id: transferId,
    target_recipient_device_id: runtime.deviceId,
    verified_source_sha256: prepared.sourceSha256
  });
  if (receiptError) throw receiptError;
  return true;
}
